/**
 * Product compare popup — <compare-popup>
 *
 * Builds the comparison table for the collection grid's Compare mode.
 *
 * sections/compare-popup.liquid renders only the LEFT LABEL COLUMN (one
 * <tr data-row="…"> per section block, in block order). This element fetches
 * each selected product through the Shopify Section Rendering API in that
 * product's own URL context, lifts the matching <td> for every row out of the
 * response's [data-compare-rows] template, and appends it as a column — so
 * each product's data stays aligned to the right row by its data-row tag, and
 * the merchant's block order/labels drive the table with no code change.
 *
 * State comes from window.theme.compare (assets/theme.js). This file READS the
 * list and may remove() from it; it must never touch compare mode.
 *
 * See Blueprint/product-compare/COMPARE_POPUP_SECTION_REARCHITECTURE_PLAN.md
 */
(function () {
  "use strict";

  var store = window.theme && window.theme.compare;
  if (!store) return;

  /* In the theme editor, top the table up to this many columns with
     placeholders so the merchant can style it with nothing selected. */
  var DESIGN_MIN_COLUMNS = 3;

  if (window.customElements.get("compare-popup")) return;

  window.customElements.define(
    "compare-popup",
    class ComparePopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector(".compare-popup__dialog");
        this.table = this.querySelector("[data-compare-table]");
        this.placeholderTemplate = this.querySelector("[data-compare-placeholder]");
        this.isDesignMode = this.dataset.designMode === "true";
        this.min = parseInt(this.dataset.min, 10) || store.MIN;
        /* Whatever id Shopify assigned this section (its key in
           sections/footer-group.json) — passed down from Liquid so the
           Section Rendering API request can never drift out of sync. */
        this.fetchSectionId = this.dataset.fetchSectionId || "compare-popup";
        this.cache = {};

        this.onOpenRequest = this.open.bind(this);
        this.onListChange = this.handleListChange.bind(this);
        this.onKeyup = this.handleKeyup.bind(this);
        this.onSectionSelect = this.handleSectionSelect.bind(this);
        this.onSectionDeselect = this.handleSectionDeselect.bind(this);

        document.addEventListener(store.OPEN_EVENT, this.onOpenRequest);
        document.addEventListener(store.LIST_EVENT, this.onListChange);

        this.querySelectorAll("[data-compare-close]").forEach(
          function (el) {
            el.addEventListener("click", this.close.bind(this));
          }.bind(this)
        );

        if (this.isDesignMode) {
          document.addEventListener("shopify:section:select", this.onSectionSelect);
          document.addEventListener("shopify:section:deselect", this.onSectionDeselect);
        }
      }

      disconnectedCallback() {
        document.removeEventListener(store.OPEN_EVENT, this.onOpenRequest);
        document.removeEventListener(store.LIST_EVENT, this.onListChange);
        document.removeEventListener("keyup", this.onKeyup);
        document.removeEventListener("shopify:section:select", this.onSectionSelect);
        document.removeEventListener("shopify:section:deselect", this.onSectionDeselect);
        document.body.classList.remove("compare-popup-open");
      }

      get isOpen() {
        return this.hasAttribute("open");
      }

      /* ------------------------------------------------ open / close -- */

      open() {
        if (!this.isDesignMode && store.getList().length < this.min) return;
        this.setAttribute("open", "");
        document.body.classList.add("compare-popup-open");
        document.addEventListener("keyup", this.onKeyup);
        this.build().then(
          function () {
            /* trapFocus / removeTrapFocus are the theme's own globals from
               assets/theme.js — reused rather than reimplemented. */
            if (typeof window.trapFocus === "function" && this.isOpen) {
              window.trapFocus(this, this.dialog);
            }
          }.bind(this)
        );
      }

      close() {
        if (!this.isOpen) return;
        this.removeAttribute("open");
        document.body.classList.remove("compare-popup-open");
        document.removeEventListener("keyup", this.onKeyup);
        if (typeof window.removeTrapFocus === "function") window.removeTrapFocus();
      }

      handleKeyup(event) {
        if (event.key === "Escape") this.close();
      }

      handleSectionSelect(event) {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.open();
      }

      handleSectionDeselect(event) {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.close();
      }

      handleListChange() {
        if (!this.isOpen) return;
        if (!this.isDesignMode && store.getList().length < this.min) {
          this.close();
          return;
        }
        this.build();
      }

      /* ------------------------------------------------ table build --- */

      /** How many placeholder columns to append after the real ones. */
      placeholderCount(real) {
        if (this.isDesignMode) {
          return real < DESIGN_MIN_COLUMNS ? DESIGN_MIN_COLUMNS - real : 0;
        }
        if (real === 0) return 3;
        if (real === 1) return 2;
        return 0;
      }

      /**
       * Gate the WHOLE table (hidden + spinner) until every column has been
       * fetched and appended, then reveal it fully populated — never a
       * "labels first, data after" flash.
       */
      build() {
        if (!this.table) return Promise.resolve();
        var handles = store.getList().slice();

        this.classList.add("is-loading");
        this.clearColumns();

        return Promise.all(
          handles.map(
            function (handle) {
              return this.fetchCells(handle);
            }.bind(this)
          )
        )
          .then(
            function (columns) {
              columns.forEach(
                function (cells, index) {
                  if (cells) this.appendColumn(cells, handles[index], false);
                }.bind(this)
              );

              var placeholders = this.placeholderCount(handles.length);
              if (placeholders && this.placeholderTemplate) {
                for (var i = 0; i < placeholders; i += 1) {
                  this.appendColumn(
                    this.extractCells(this.placeholderTemplate.content.cloneNode(true)),
                    null,
                    true
                  );
                }
              }
            }.bind(this)
          )
          .catch(function (error) {
            console.warn("[compare-popup] build failed:", error);
          })
          .then(
            function () {
              /* Always clear the gate — a failed fetch must never leave the
                 popup stuck on the spinner. */
              this.classList.remove("is-loading");
            }.bind(this)
          );
      }

      /** Remove every appended column, leaving only the label cell per row. */
      clearColumns() {
        this.table.querySelectorAll("tr[data-row]").forEach(function (tr) {
          while (tr.children.length > 1) tr.removeChild(tr.lastElementChild);
        });
      }

      /** Map row name -> <td> from a fragment of <tr data-row><td>…</td></tr>. */
      extractCells(fragment) {
        var map = {};
        fragment.querySelectorAll("tr[data-row]").forEach(function (tr) {
          var td = tr.querySelector("td");
          if (td) map[tr.dataset.row] = td;
        });
        return map;
      }

      fetchCells(handle) {
        var snapshot = store.getSnapshot(handle);
        var url = (snapshot && snapshot.url) || "/products/" + handle;

        /* Skip the cache in design mode so First-row setting changes show on
           the next open instead of serving a stale fetched head cell. */
        if (!this.isDesignMode && this.cache[url]) {
          return Promise.resolve(this.extractCells(this.cache[url].cloneNode(true)));
        }

        var separator = url.indexOf("?") === -1 ? "?" : "&";
        /* no-store: a saved section change must not be served stale out of
           the browser HTTP cache. */
        return fetch(url + separator + "section_id=" + this.fetchSectionId, {
          cache: "no-store"
        })
          .then(function (response) {
            return response.ok ? response.text() : "";
          })
          .then(
            function (html) {
              if (!html) return null;
              var doc = new DOMParser().parseFromString(html, "text/html");
              var template = doc.querySelector("[data-compare-rows]");
              if (!template) return null;
              /* Cache the raw fragment; clone per use so appending never
                 consumes the cached copy. */
              if (!this.isDesignMode) this.cache[url] = template.content;
              return this.extractCells(template.content.cloneNode(true));
            }.bind(this)
          )
          .catch(function () {
            return null;
          });
      }

      appendColumn(cellsByRow, handle, isPlaceholder) {
        this.table.querySelectorAll("tr[data-row]").forEach(
          function (tr) {
            var rowName = tr.dataset.row;
            var source = cellsByRow[rowName];
            var td;

            if (source) {
              td = source.cloneNode(true);
            } else {
              td = document.createElement("td");
              td.className = "compare-popup__cell";
              td.innerHTML = '<span class="compare-popup__dash">&mdash;</span>';
            }
            if (isPlaceholder) td.classList.add("is-placeholder");
            tr.appendChild(td);

            if (rowName === "head") {
              this.applyHeadSettings(td);
              var removeBtn = td.querySelector("[data-compare-remove]");
              if (removeBtn) {
                if (isPlaceholder || !handle) {
                  removeBtn.remove();
                } else {
                  /* Reuse the one delegated remove listener that already
                     handles the sticky bar's slots. */
                  removeBtn.setAttribute("data-compare-bar-remove", handle);
                }
              }
            }
          }.bind(this)
        );
      }

      /**
       * Apply the section's First-row settings, read live off the shell's
       * data-* attributes (NOT the fetched HTML) so they are correct before a
       * Save in the editor, immune to a stale cached fetch, and identical for
       * real and placeholder columns.
       */
      applyHeadSettings(td) {
        var showTitle = this.dataset.showTitle === "true";
        var showPrice = this.dataset.showPrice === "true";
        var showButton = this.dataset.showButton === "true";
        var buttonStyle = this.dataset.buttonStyle || "primary";

        if (!showTitle) {
          var title = td.querySelector("[data-compare-title]");
          if (title) title.remove();
        }
        if (!showPrice) {
          var price = td.querySelector("[data-compare-price]");
          if (price) price.remove();
        }

        var button = td.querySelector("[data-compare-button]");
        if (!button) return;
        if (!showButton) {
          button.remove();
          return;
        }
        button.classList.toggle("button--secondary", buttonStyle === "secondary");
      }
    }
  );
})();
